import { Eye, Lightbulb, Loader2, Lock, NotebookPen, Pencil, Pin, Plus, Trash2, Users } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Section } from '@/components/common/Blocks'
import { EmptyState } from '@/components/common/States'
import { Badge, type BadgeTone } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { confirmModal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { fmtDateTime } from '../../../shared/format'
import { type AccountNote, type NoteInput, type NoteKind, type NotePotential, type NoteVisibility, useAccountNotes, useNoteMutations } from '../../notes'
import { toastError } from '../../ui'

const VIS_ICON = { private: Lock, team: Users, all: Eye }
const POTENTIAL_TONE: Record<Exclude<NotePotential, ''>, BadgeTone> = { low: 'neutral', medium: 'warning', high: 'success' }
const EMPTY: NoteInput = { kind: 'note', visibility: 'all', title: '', body: '', needs: '', solution: '', potential: '', pinned: false }

/**
 * Müşteriye özel notlar ve satış öncesi ön analiz. Görünürlük not bazında seçilir; bir çalışan
 * başkasının notunu değiştiremez/silemez (yalnız yazar veya yetkili yönetici).
 */
export function NotesTab({ customerId, canWrite }: { customerId: string; canWrite: boolean }) {
  const { t } = useTranslation()
  const notes = useAccountNotes(customerId)
  const { save, remove } = useNoteMutations(customerId)
  const [editing, setEditing] = useState<{ id?: string; input: NoteInput } | null>(null)
  const [filter, setFilter] = useState<'all' | NoteKind>('all')

  const rows = (notes.data ?? []).filter((n) => filter === 'all' || n.kind === filter)
  const onSave = () => {
    if (!editing) return
    save.mutate(editing, {
      onSuccess: () => {
        toast.success(t('crm.notes.saved'))
        setEditing(null)
      },
      onError: toastError,
    })
  }
  const onDelete = async (n: AccountNote) => {
    if (!(await confirmModal({ title: t('crm.notes.deleteTitle'), description: t('crm.notes.deleteBody'), confirmLabel: t('common.delete'), destructive: true }))) return
    remove.mutate(n.id, { onSuccess: () => toast.success(t('common.deleted')), onError: toastError })
  }
  const set = <K extends keyof NoteInput>(k: K, v: NoteInput[K]) => setEditing((e) => (e ? { ...e, input: { ...e.input, [k]: v } } : e))

  return (
    <Section
      title={t('crm.notes.title')}
      icon={NotebookPen}
      bodyClassName="p-0"
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <Select
            size="sm"
            value={filter}
            onChange={(v) => setFilter(v)}
            options={(['all', 'note', 'analysis'] as const).map((k) => ({ value: k, label: t(`crm.notes.filter.${k}`) }))}
            aria-label={t('crm.notes.filterLabel')}
          />
          {canWrite ? (
            <>
              <Button size="sm" variant="outline" onClick={() => setEditing({ input: { ...EMPTY, kind: 'analysis', visibility: 'team' } })}>
                <Lightbulb />
                {t('crm.notes.addAnalysis')}
              </Button>
              <Button size="sm" onClick={() => setEditing({ input: { ...EMPTY } })}>
                <Plus />
                {t('crm.notes.addNote')}
              </Button>
            </>
          ) : null}
        </div>
      }
    >
      {editing ? (
        <div className="space-y-3 border-b bg-muted/30 p-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="nt-kind">{t('crm.notes.kindLabel')}</Label>
              <Select id="nt-kind" value={editing.input.kind} onChange={(v) => set('kind', v)} options={(['note', 'analysis'] as const).map((k) => ({ value: k, label: t(`crm.notes.kind.${k}`) }))} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="nt-vis">{t('crm.notes.visibilityLabel')}</Label>
              <Select<NoteVisibility> id="nt-vis" value={editing.input.visibility} onChange={(v) => set('visibility', v)} options={(['all', 'team', 'private'] as const).map((k) => ({ value: k, label: t(`crm.notes.visibility.${k}`) }))} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="nt-title">{t('crm.notes.titleLabel')}</Label>
              <Input id="nt-title" value={editing.input.title} maxLength={200} onChange={(e) => set('title', e.target.value)} />
            </div>
          </div>
          {editing.input.kind === 'analysis' ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="nt-needs">{t('crm.notes.needs')}</Label>
                <Textarea id="nt-needs" rows={3} value={editing.input.needs} placeholder={t('crm.notes.needsPlaceholder')} onChange={(e) => set('needs', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="nt-solution">{t('crm.notes.solution')}</Label>
                <Textarea id="nt-solution" rows={3} value={editing.input.solution} placeholder={t('crm.notes.solutionPlaceholder')} onChange={(e) => set('solution', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="nt-pot">{t('crm.notes.potentialLabel')}</Label>
                <Select
                  id="nt-pot"
                  value={editing.input.potential || 'unknown'}
                  onChange={(v) => set('potential', v === 'unknown' ? '' : (v as NotePotential))}
                  options={(['unknown', 'low', 'medium', 'high'] as const).map((k) => ({ value: k, label: t(`crm.notes.potential.${k}`) }))}
                />
              </div>
            </div>
          ) : null}
          <div className="space-y-1.5">
            <Label htmlFor="nt-body">{t(editing.input.kind === 'analysis' ? 'crm.notes.research' : 'crm.notes.body')}</Label>
            <Textarea id="nt-body" rows={4} value={editing.input.body} onChange={(e) => set('body', e.target.value)} />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" className="size-4 accent-primary" checked={editing.input.pinned} onChange={(e) => set('pinned', e.target.checked)} />
              {t('crm.notes.pin')}
            </label>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                {t('common.cancel')}
              </Button>
              <Button size="sm" onClick={onSave} disabled={save.isPending}>
                {save.isPending ? <Loader2 className="animate-spin" /> : null}
                {t('common.save')}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {notes.isLoading ? (
        <Skeleton className="m-4 h-32" />
      ) : !rows.length ? (
        <EmptyState icon={NotebookPen} title={t('crm.notes.empty')} description={t('crm.notes.emptyHint')} />
      ) : (
        <ul className="divide-y">
          {rows.map((n) => {
            const VisIcon = VIS_ICON[n.visibility]
            return (
              <li key={n.id} className={cn('space-y-2 px-4 py-3', n.pinned && 'bg-warning/[0.05]')}>
                <div className="flex flex-wrap items-center gap-2">
                  {n.pinned ? <Pin className="size-4 text-warning" aria-label={t('crm.notes.pinned')} /> : null}
                  <Badge tone={n.kind === 'analysis' ? 'info' : 'neutral'}>{t(`crm.notes.kind.${n.kind}`)}</Badge>
                  {n.potential ? <Badge tone={POTENTIAL_TONE[n.potential]}>{t(`crm.notes.potential.${n.potential}`)}</Badge> : null}
                  <span className="min-w-0 flex-1 truncate font-medium">{n.title || (n.kind === 'analysis' ? t('crm.notes.kind.analysis') : '')}</span>
                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" title={t(`crm.notes.visibility.${n.visibility}`)}>
                    <VisIcon className="size-3.5" aria-hidden />
                    {t(`crm.notes.visibility.${n.visibility}`)}
                  </span>
                  {n.canEdit ? (
                    <>
                      <Button size="icon" variant="ghost" className="size-8" aria-label={t('common.edit')} onClick={() => setEditing({ id: n.id, input: { kind: n.kind, visibility: n.visibility, title: n.title, body: n.body, needs: n.needs, solution: n.solution, potential: n.potential, pinned: n.pinned } })}>
                        <Pencil />
                      </Button>
                      <Button size="icon" variant="ghost" className="size-8" aria-label={t('common.delete')} onClick={() => void onDelete(n)}>
                        <Trash2 />
                      </Button>
                    </>
                  ) : null}
                </div>
                {n.needs ? (
                  <p className="text-sm">
                    <span className="font-medium">{t('crm.notes.needs')}:</span> <span className="whitespace-pre-wrap text-muted-foreground">{n.needs}</span>
                  </p>
                ) : null}
                {n.solution ? (
                  <p className="text-sm">
                    <span className="font-medium">{t('crm.notes.solution')}:</span> <span className="whitespace-pre-wrap text-muted-foreground">{n.solution}</span>
                  </p>
                ) : null}
                {n.body ? <p className="whitespace-pre-wrap text-sm text-muted-foreground">{n.body}</p> : null}
                <p className="text-xs text-muted-foreground">{t('crm.notes.byline', { name: n.authorName || '—', at: fmtDateTime(n.createdAt) })}</p>
              </li>
            )
          })}
        </ul>
      )}
    </Section>
  )
}
